/**
 * Shared diagnostic-report PDF builder — the single place that turns a
 * `diagnostic_reports` row into a base64 PDF attachment.
 *
 * Mirrors buildServiceHistoryPdf: fetch live org identity, render the document,
 * hand back base64 plus a filename. One builder so every entry point (the
 * customer email composer today, the diagnostic modal's own "email this"
 * tomorrow) attaches the identical document rather than one of them quietly
 * sending a message with nothing on it.
 */
import { pdf } from '@react-pdf/renderer';
import { supabase } from '../../../../lib/supabase';
import { urlToBase64, LOGO_MAX_PX } from '../../../../lib/images';
import { reportError } from '../../../../lib/errors';
import {
    DiagnosticReportPDF,
    type DiagnosticIdentity,
    type DiagnosticPDFFinding,
    type DiagnosticPDFLineItem,
    type DiagnosticSeverity,
} from './DiagnosticReportPDF';
import { motOdometerOffer } from '../../../../lib/vehicle';
import { mileageLine } from '../../Customers/mileage';
import { DEFAULT_DOCUMENT_ACCENT } from '../../../../lib/brandAccent';

export interface BuildDiagnosticPdfArgs {
    orgId: string;
    reportId: string;
    customerName: string;
    customerEmail?: string;
    customerPhone?: string;
}

export interface BuiltDiagnosticPdf {
    pdfBase64: string;
    fileName: string;
    findingCount: number;
}

interface OrgIdentityRow {
    name?: string | null;
    branding?: { logo_url?: string; invoice_accent_color?: string; primary_color?: string } | null;
    business_address?: string | null;
    business_phone?: string | null;
    business_email?: string | null;
    vat_number?: string | null;
    subscription_tier?: string | null;
}

interface ReportRow {
    id: string;
    created_at: string;
    summary?: string | null;
    vehicle_id?: string | null;
    vehicle_vrm?: string | null;
    vehicle_desc?: string | null;
    findings?: unknown;
    line_items?: unknown;
}

interface VehicleRow {
    vrm?: string | null;
    make?: string | null;
    model?: string | null;
    year?: number | null;
    mileage?: number | null;
    mot_last_odometer_value?: string | null;
    mot_last_odometer_unit?: string | null;
    mot_last_test_date?: string | null;
    /** Formatted for the report: the recorded mileage, or the last MOT
     *  reading labelled as one. Never an MOT figure without its label. */
    mileageText?: string | null;
}

const VALID_SEVERITIES: DiagnosticSeverity[] = ['critical', 'advisory', 'monitor'];

/**
 * `findings` and `line_items` are jsonb, so anything could be in them — an
 * older report shape, a hand-edited row, null. Normalise defensively rather
 * than letting a malformed entry throw halfway through rendering and produce
 * an email with no attachment.
 */
const normaliseFindings = (raw: unknown): DiagnosticPDFFinding[] => {
    if (!Array.isArray(raw)) return [];
    return raw.map((f) => {
        const r = (f ?? {}) as Record<string, unknown>;
        const severity = String(r.severity ?? 'advisory') as DiagnosticSeverity;
        return {
            system: String(r.system ?? 'General'),
            title: String(r.title ?? '').trim(),
            severity: VALID_SEVERITIES.includes(severity) ? severity : 'advisory',
            notes: typeof r.notes === 'string' ? r.notes : '',
            photoCount: Array.isArray(r.photos) ? r.photos.length : 0,
        };
    }).filter((f) => f.title.length > 0);
};

const normaliseLineItems = (raw: unknown): DiagnosticPDFLineItem[] => {
    if (!Array.isArray(raw)) return [];
    return raw.map((li) => {
        const r = (li ?? {}) as Record<string, unknown>;
        return {
            type: typeof r.type === 'string' ? r.type : undefined,
            description: String(r.description ?? '').trim(),
            quantity: Number(r.quantity ?? 0) || 0,
            unitPrice: Number(r.unitPrice ?? 0) || 0,
        };
    }).filter((li) => li.description.length > 0);
};

export async function buildDiagnosticPdfBase64(
    args: BuildDiagnosticPdfArgs
): Promise<BuiltDiagnosticPdf | null> {
    const { orgId, reportId, customerName, customerEmail, customerPhone } = args;

    try {
        const [{ data: org, error: orgError }, { data: report, error: repError }] = await Promise.all([
            supabase
                .from('organizations')
                .select('name, branding, business_address, business_phone, business_email, vat_number, subscription_tier')
                .eq('id', orgId)
                .single<OrgIdentityRow>(),
            supabase
                .from('diagnostic_reports')
                .select('id, created_at, summary, vehicle_id, vehicle_vrm, vehicle_desc, findings, line_items')
                .eq('id', reportId)
                .single<ReportRow>(),
        ]);
        if (orgError) reportError(orgError, 'build-diagnostic-pdf.organizations');
        if (repError) throw repError;
        if (!report) return null;

        const branding = org?.branding || {};
        const identity: DiagnosticIdentity = {
            name: org?.name || 'Your Workshop',
            logo_url: branding.logo_url || undefined,
            address: org?.business_address || undefined,
            phone: org?.business_phone || undefined,
            email: org?.business_email || undefined,
            vat_number: org?.vat_number || undefined,
            accent: branding.invoice_accent_color || branding.primary_color || DEFAULT_DOCUMENT_ACCENT,
            tier: org?.subscription_tier || 'free',
        };

        // The report denormalises vrm/desc at save time; the vehicle row has the
        // rest. Read it when we can, fall back to what is on the report — a
        // walk-in has no vehicle_id at all.
        let vehicle: VehicleRow | null = null;
        if (report.vehicle_id) {
            const { data: v, error: vErr } = await supabase
                .from('vehicles')
                .select('vrm, make, model, year, mileage, mot_last_odometer_value, mot_last_odometer_unit, mot_last_test_date')
                .eq('id', report.vehicle_id)
                .maybeSingle<VehicleRow>();
            if (vErr) reportError(vErr, 'build-diagnostic-pdf.vehicles');
            vehicle = v ?? null;
        }
        if (vehicle) {
            // The same order as the service history: the recorded reading,
            // then the last MOT reading, which prints as "at last MOT, <date>"
            // because it can be up to a year old (Customers/mileage.ts).
            const mot = motOdometerOffer(vehicle);
            vehicle.mileageText = vehicle.mileage != null
                ? mileageLine(vehicle.mileage, 'vehicle')
                : mot ? mileageLine(mot.miles, 'mot', mot.testDate) : null;
        }
        if (!vehicle) {
            const [make, ...rest] = (report.vehicle_desc || '').split(' ');
            vehicle = {
                vrm: report.vehicle_vrm ?? null,
                make: make || null,
                model: rest.join(' ') || null,
                year: null,
            };
        }

        const findings = normaliseFindings(report.findings);
        const lineItems = normaliseLineItems(report.line_items);

        let logoBase64: string | undefined;
        if (branding.logo_url) {
            try {
                logoBase64 = (await urlToBase64(branding.logo_url, { maxDimension: LOGO_MAX_PX })) ?? undefined;
            } catch (e) {
                console.error('Logo conversion failed:', e);
            }
        }

        const stamp = new Date(report.created_at).toISOString().slice(0, 10);
        const fileName = `Health_Check_${(vehicle.vrm || customerName || 'vehicle').replace(/[^a-z0-9]+/gi, '_')}_${stamp}.pdf`;

        const doc = (
            <DiagnosticReportPDF
                identity={{ ...identity, logoBase64 }}
                customer={{ full_name: customerName, email: customerEmail, phone: customerPhone }}
                vehicle={vehicle}
                findings={findings}
                lineItems={lineItems}
                summary={report.summary}
                reportDate={report.created_at}
            />
        );
        const blob = await pdf(doc).toBlob();
        const pdfBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

        return { pdfBase64, fileName, findingCount: findings.length };
    } catch (err) {
        console.error('buildDiagnosticPdfBase64 failed:', err);
        return null;
    }
}
